import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { ProjectFeedSkeleton } from '../../components/dashboard/ProjectFeedSkeleton';
import { ProjectsEmptyState } from '../../components/dashboard/ProjectsEmptyState';
import { Input } from '../../components/ui/Input';
import { ProjectCard } from '../../features/projects/ProjectCard';
import { ProjectModal } from '../../features/projects/ProjectModal';
import { ProjectPromptComposer } from '../../features/projects/ProjectPromptComposer';
import {
  isProductCreateForbiddenMessage,
  ProductCreateBanner,
  ProductCreateErrorMessage
} from '../../features/projects/ProductCreateBanner';
import { fetchMyPackageApi } from '../../api/packages.api';
import { PublicTemplatesSection } from '../../features/publicProjects/PublicTemplatesSection';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';
import {
  createProject,
  fetchProjects,
  setActiveTab,
  setSearch
} from '../../redux/slices/projectsSlice';
import {
  buildProjectBootstrapMessage,
  createProjectNameFromPrompt
} from '../../features/projects/projectFormUtils';
import { DEFAULT_LLM_MODEL_KEY } from '../../constants/llmModels';
import { uploadReferenceImages } from '../../utils/referenceImages';

export const ProjectsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useI18n();
  const { items, search, loading, hasLoaded, error, activeTab } = useAppSelector((state) => state.projects);
  const [modalOpen, setModalOpen] = useState(false);
  const [projectDraft, setProjectDraft] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [creating, setCreating] = useState(false);
  const [entitlement, setEntitlement] = useState(null);
  const promptInputRef = useRef(null);
  const currentTab = activeTab === 'ready' ? 'ready' : 'your';
  const productLocked = Boolean(entitlement) && !entitlement.canCreateProduct;

  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    fetchMyPackageApi()
      .then((current) => {
        if (!cancelled) {
          setEntitlement(current);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEntitlement(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredProjects = useMemo(() => {
    const query = search.trim().toLowerCase();

    return items.filter((project) => {
      if (!query) {
        return true;
      }

      return `${project.name} ${project.description}`.toLowerCase().includes(query);
    });
  }, [items, search]);

  const openCreateModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const focusComposer = useCallback(() => {
    dispatch(setActiveTab('your'));
    window.requestAnimationFrame(() => {
      promptInputRef.current?.focus();
      promptInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [dispatch]);

  const openEditPage = useCallback(
    (project) => {
      navigate(`/projects/${project.id}/edit`);
    },
    [navigate]
  );

  useEffect(() => {
    if (location.state?.openCreateProject) {
      setProjectDraft(location.state?.projectDraft || null);
      if (location.state?.projectDraft?.description) {
        setPrompt(location.state.projectDraft.description);
      }
      openCreateModal();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate, openCreateModal]);

  useEffect(() => {
    if (location.state?.editProjectId) {
      navigate(`/projects/${location.state.editProjectId}/edit`, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const handleSaveProject = async (form, { onUploadProgress } = {}) => {
    const { referenceImages = [], features = [], ...projectForm } = form;
    setCreating(true);

    try {
      const action = await dispatch(createProject({ ...projectForm, features }));

      if (!createProject.fulfilled.match(action)) {
        return false;
      }

      let imageUrls = [];

      if (referenceImages.length > 0) {
        const uploadResult = await uploadReferenceImages({
          projectId: action.payload.id,
          files: referenceImages,
          onProgress: onUploadProgress
        });
        imageUrls = uploadResult.urls;
      }

      setModalOpen(false);
      setProjectDraft(null);
      setPrompt('');
      setAttachments([]);
      navigate(`/workspace/${action.payload.id}`, {
        state: {
          bootstrapChat: {
            message: buildProjectBootstrapMessage(form.name, form.description),
            model: form.model,
            imageUrls
          }
        }
      });
      return true;
    } finally {
      setCreating(false);
    }
  };

  const handlePromptSubmit = () => {
    const description = prompt.trim();
    const referenceImages = attachments.map((item) => item.file).filter(Boolean);
    if ((!description && referenceImages.length === 0) || creating || productLocked) {
      return;
    }

    const hasDesignAttachment = attachments.some((item) => item.purpose === 'design');
    setProjectDraft({
      name: createProjectNameFromPrompt(description, language),
      description: description || (hasDesignAttachment ? t('designFromUiPrompt') : ''),
      model: DEFAULT_LLM_MODEL_KEY,
      referenceImages
    });
    setModalOpen(true);
  };

  const hasSearch = search.trim().length > 0;
  const emptyTitle = hasSearch ? t('projectsSearchEmpty') : t('noProjects');
  const emptyBody = hasSearch ? t('projectsSearchEmptyBody') : t('projectsEmptyComposerBody');
  const suggestions = t('landingSuggestions') || [];

  return (
    <DashboardLayout title={t('productGeneration')}>
      <section className="w-full">
        <div className="mx-auto mb-8 max-w-3xl text-center sm:mb-10">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-950 sm:text-3xl dark:text-white">
            {t('projectsComposerTitle')}
          </h1>
          <p className="mt-2 text-sm text-neutral-500">{t('projectsPageSubtitle')}</p>
          <div className="mt-6 text-start">
            <ProductCreateBanner entitlement={entitlement} />
            <ProjectPromptComposer
              value={prompt}
              onChange={setPrompt}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              loading={creating}
              disabled={productLocked}
              onSubmit={handlePromptSubmit}
              inputRef={promptInputRef}
              suggestions={suggestions}
            />
          </div>
          {error ? (
            <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-start text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200">
              {isProductCreateForbiddenMessage(error) ? <ProductCreateErrorMessage /> : error}
            </div>
          ) : null}
        </div>

        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div
            role="tablist"
            aria-label={t('projects')}
            className="inline-flex w-full rounded-xl bg-[#F3F4F6] p-1 sm:w-auto dark:bg-neutral-900"
          >
            {[
              { id: 'your', label: t('yourProjects') },
              { id: 'ready', label: t('readyProducts') }
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={currentTab === tab.id}
                onClick={() => dispatch(setActiveTab(tab.id))}
                className={classNames(
                  'flex-1 rounded-lg px-4 py-2 text-sm font-medium transition sm:flex-none sm:min-w-[148px]',
                  currentTab === tab.id
                    ? 'bg-white text-neutral-950 shadow-sm dark:bg-neutral-800 dark:text-white'
                    : 'text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-200'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {currentTab === 'your' ? (
            <div className="relative w-full sm:max-w-xs">
              <Search size={15} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input
                className="w-full rounded-lg border-neutral-200 bg-white ps-9 dark:border-neutral-800 dark:bg-neutral-950"
                placeholder={t('searchPlaceholder')}
                value={search}
                onChange={(event) => dispatch(setSearch(event.target.value))}
              />
            </div>
          ) : null}
        </div>

        {currentTab === 'your' ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {loading && !hasLoaded && items.length === 0 ? (
              <ProjectFeedSkeleton count={6} />
            ) : filteredProjects.length === 0 ? (
              <ProjectsEmptyState
                title={emptyTitle}
                body={emptyBody}
                actionLabel={hasSearch ? undefined : t('projectsEmptyComposerCta')}
                onAction={hasSearch ? undefined : focusComposer}
              />
            ) : (
              filteredProjects.map((project) => (
                <ProjectCard key={project.id} project={project} onEdit={openEditPage} />
              ))
            )}
          </div>
        ) : (
          <PublicTemplatesSection variant="page" onCreateProject={focusComposer} />
        )}
      </section>

      <ProjectModal
        open={modalOpen}
        initialProject={projectDraft}
        error={error}
        productLocked={productLocked}
        onClose={() => {
          setModalOpen(false);
          setProjectDraft(null);
        }}
        onSave={handleSaveProject}
      />
    </DashboardLayout>
  );
};
